import { HttpStatus } from './http-status.util';
import { TOAST_DURATION_LONG_MS } from './toast-durations.util';

/**
 * The vocabulary of the filing-load diagnostic code — the short, greppable
 * string printed inside every "this sheet did not load" toast.
 *
 * ## Why this exists
 *
 * Before this file the entire filing-load failure surface was three sentences
 * with no identifying detail:
 *
 *   - `Failed to load MBRS data`               (container)
 *   - `Failed to load form schema`             (every sheet)
 *   - `Failed to load form. Please refresh the page.`  (17 of the 26 sheets)
 *
 * A screenshot of any of them told a developer nothing: not which sheet, not
 * which conversion, not whether the request had even left the browser, and —
 * for the worst failure mode of all, the perpetual spinner — nothing at all,
 * because a promise that never settles throws nothing for a `catch` to report.
 *
 * A reporter can reliably screenshot a toast. So the toast carries a short,
 * greppable diagnostic code — the classification of how the load ended. The
 * step ledger that produced it (which steps completed, which one did not) is
 * **console-only**: it is the detail a developer reads with the machine in
 * hand, not something a reporter can usefully transcribe off a screenshot, and
 * keeping it off the toast keeps the code itself short and stable per scope.
 *
 * ## Reading a code
 *
 *   MBRS-S13-ASSET-a1b2c3d4
 *   ---- --- ----- --------
 *    |    |    |      `- first 8 characters of the conversion id
 *    |    |    `-------- how it ended (see LoadFailureClass)
 *    |    `------------- scope: `S<n>` = sheet role n (a conversion-wide
 *    |                   failure names the sheet being reached, too)
 *    `----------------------- fixed prefix, so the code greps cleanly
 *
 * `-` separates the fields and never appears inside one: a role label's own
 * hyphen prints as `_` (`MBRS-S15_410000-NET-a1b2c3d4`), as do page scopes
 * (`MBRS-XBRL_FILES-NET`). See {@link toDiagnosticScopeToken}.
 *
 * The `ASSET` class reads as "the request for the form-definition JSON came
 * back non-OK". **Which step broke is in the console, not the code** —
 * `[filing-load] MBRS-S13-ASSET-a1b2c3d4 — steps reached: [1,2,4,5]` says step 3
 * (the schema fetch) did not complete. Steps are recorded as they COMPLETE,
 * never derived at throw time, which is what makes the console gap meaningful;
 * see {@link SheetLoadTraceService}.
 *
 * ## Extending
 *
 * Adding a step means adding it to the relevant `*_LOAD_STEPS` map here AND
 * marking it at its real completion point. Adding a failure class means adding
 * a `LOAD_FAILURE_COPY` entry — the compiler enforces that, so a new class
 * cannot ship without a human-readable sentence.
 *
 * @see docs/development/filing-load-diagnostics.md — the full contract: the
 *      per-sheet rules, the watchdog lifecycle, and how to act on a reported
 *      code.
 */

/**
 * How a filing load ended. Deliberately short and upper-case: these appear
 * verbatim in the toast a reporter screenshots, and in log searches.
 *
 * The first five are TRANSPORT outcomes — they describe what happened to the
 * HTTP request itself and are only distinguishable because
 * `RetrieveMbrsDataService` now preserves the status instead of collapsing every
 * failure into one opaque shape. `NET` in particular is the answer to "was this
 * a network outage?": it means no response ever reached the browser.
 */
export type LoadFailureClass =
  /** No response ever reached the browser — offline, DNS, CORS, TLS, or the request timed out. */
  | 'NET'
  /** HTTP 401. The session is gone or was never established. */
  | 'AUTH'
  /** HTTP 403. Authenticated, but not permitted to read this conversion. */
  | 'PERM'
  /**
   * HTTP 5xx, or any outcome that reached us but is not a definite rejection of
   * the request — an unparseable 2xx body, an unclassifiable thrown value. The
   * finer detail (status, error text) is console-only; see `classifyHttpStatus`.
   */
  | 'SRV'
  /**
   * The server answered and REJECTED the request (4xx other than 401/403). On a
   * conversion-loading call this is "the conversion does not exist" — an
   * unknown or malformed id. Replaces the retired `REQ` class.
   */
  | 'RESP'
  /**
   * The MSIC code list (`msic_options.json`) was unavailable — missing, unreadable
   * or empty — so role 2's MSIC dropdowns have no options. Always a DEGRADED
   * report (`LoadTrace.warn`): the sheet still opens. This is the ONLY producer;
   * a successful conversion load with no sheet data is a normal blank conversion,
   * not a failure (the container's old `EMPTY` branch was unreachable and removed).
   */
  | 'EMPTY'
  /** A form-definition JSON asset answered non-OK — missing or misdeployed file. */
  | 'ASSET'
  /** A payload arrived but could not be parsed or was not the expected shape. */
  | 'PARSE'
  /** Data was in hand; constructing the form or replaying saved state threw. */
  | 'BUILD'
  /** Nothing threw. The load simply never reached its terminal step within the watchdog window. */
  | 'STALL';

/**
 * The generic "we don't have a more specific sentence for this" copy, shared by
 * every class whose only actionable next step is reporting the code — SRV and
 * its four lookalikes below. One constant rather than five copies of the same
 * string, so a future wording change can't update four of five by mistake.
 */
const SUPPORT_CODE_COPY =
  'Something went wrong while preparing this page. Please contact your admin with the support code.';

/**
 * The human-readable half of the toast, one sentence per failure class.
 *
 * These are the words the preparer reads; the code beside them is for us. Each
 * says what happened in the user's terms and, where there is one, what they can
 * do about it. None of them says "schema" — that is our word, not theirs.
 *
 * Keyed by class rather than composed from message prose, matching the
 * service-layer rule that UI copy keys on a code and never on a server's
 * message text.
 *
 * **`AUTH` IS rendered, just not here** (P.O., 2026-10-05) — it is no longer in
 * `SILENT_FAILURE_CLASSES`' "never shown" sense; see that constant's docblock.
 * The sentence below is shown as a toast on the login page the forced-logout
 * redirect lands on, carried there by `AuthFailureNoticeService`, not in the
 * app the failure happened in (which has already navigated away by the time
 * anything could render there).
 */
export const LOAD_FAILURE_COPY: Record<LoadFailureClass, string> = {
  NET: 'Connection lost. Check your internet and try again.',
  AUTH: 'Your session has expired. Please log in again.',
  PERM: "You don't have access to this record.",
  SRV: SUPPORT_CODE_COPY,
  RESP: "We couldn't find the conversion for this record. Please contact your admin with the support code.",
  // EMPTY (role 2's MSIC code list unavailable) shares the generic sentence for now.
  // The MSIC-specific wording below is parked, NOT deleted: it still needs P.O.
  // approval and the copy may change. Restore it (and the matching expectations in
  // `sheet-load-trace.util.spec.ts` and `sheet-load-trace.service.spec.ts`, plus the
  // note in `02_scope-of-filing.md`) once the P.O. signs off.
  // EMPTY:
  //   'Something went wrong while preparing the MSIC codes for this page. Please contact your admin with the support code.',
  EMPTY: SUPPORT_CODE_COPY,
  ASSET: SUPPORT_CODE_COPY,
  PARSE: SUPPORT_CODE_COPY,
  BUILD: SUPPORT_CODE_COPY,
  STALL:
    'This is taking longer than usual. Please refresh the page. If it happens again, please contact your admin with the support code.',
};

/**
 * Failure classes that must not be shown AT THE CALL SITE that classified them.
 *
 * `AUTH` is the one member, and the reason is purely about WHERE, not whether.
 * A 401 on an authenticated session is caught by `sessionExpiryInterceptor`,
 * which calls `AuthService.forceLogout(reason)` and redirects to the login
 * page in the same tick — rendering a "this sheet didn't load" toast on the
 * page the failure happened on would be noise nobody will ever see (it is
 * navigated away from before the toast could matter) or, worse, a toast
 * stacked on top of a redirect the user has already understood.
 *
 * **It is no longer suppressed everywhere** (P.O., 2026-10-05): the call site
 * that classifies an `AUTH` failure stages its code on
 * `AuthFailureNoticeService` instead of toasting it locally, and the login
 * page — where the user actually lands — reads and shows it. `LOAD_FAILURE_COPY.AUTH`
 * is the sentence shown there. The classification was always kept either way:
 * `[filing-load] MBRS-S4-AUTH-… — steps reached: [1,2]` / `[page-load] MBRS-<SCOPE>-AUTH` still
 * reach the console unconditionally, so a developer with the log could always
 * tell a 401 apart from a 500 or an outage even before this.
 *
 * `PERM` (403) is deliberately NOT in this set. A 403 means "wrong role", not
 * "session gone" — it must never reach the forced-logout path, so nothing else
 * tells the user anything and the toast is the only explanation they get.
 *
 * Residual case, accepted: the interceptor's gate skips dev/Bearer sessions and
 * calls made before auth has resolved, so a 401 on one of those paths never
 * stages a login-page code. Neither is a preparer loading a filing, and both
 * leave the console record intact.
 */
export const SILENT_FAILURE_CLASSES: ReadonlySet<LoadFailureClass> = new Set<LoadFailureClass>([
  'AUTH',
]);

/**
 * How long EVERY `MBRS-…` diagnostic toast stays on screen — the filing-sheet
 * and container load failures (`SheetLoadTraceService`), the page-load
 * failures (`buildPageLoadFailureNotice` call sites) and the login page's
 * staged AUTH notice alike.
 *
 * One constant for the whole family, deliberately: a reporter's ability to
 * screenshot the code must not depend on which page or which class fired it.
 * Set to 5s, the flow-level default (P.O., 2026-10-08: briefly 6s the same
 * day, then reduced back to 5s). If field
 * reports still show preparers missing the code, this is the ONE line to
 * raise — `TOAST_DURATION_MAX_MS` (10s) is the intended next step, and
 * `sticky: true` the step after that.
 */
export const DIAGNOSTIC_TOAST_LIFE_MS = TOAST_DURATION_LONG_MS;

/**
 * The container's load pipeline, in execution order. The container is the
 * conversion-wide half of the load: one HTTP call plus the per-sheet populate
 * and validation-planning work that follows it.
 *
 * A console line of `MBRS-S4-NET … — steps reached: [1,2]` from this pipeline
 * (its record's `origin` is `'conversion'`) therefore says: the conversion id
 * was read and IndexedDB hydration settled, and then the fetch never came back.
 * The code prints the `S<role>` of the sheet being reached, not a scope of its
 * own — see `SheetLoadTraceService.beginContainerLoad`.
 */
export const CONTAINER_LOAD_STEPS = {
  /** A conversion id was resolved from the URL. */
  conversionIdResolved: 1,
  /** The IndexedDB hydration promise settled, so persisted state is in the subjects. */
  persistedStateHydrated: 2,
  /** A response to `retrieve-mbrs-data` reached the browser (any status). */
  responseReceived: 3,
  /** That response carried `status: true` and a usable body. */
  responseAccepted: 4,
  /** Every per-sheet populate call for this phase completed. */
  sheetsPopulated: 5,
  /** The validation plan resolved — full cache hit, partial resume, or a fresh cycle. */
  validationPlanResolved: 6,
  /** The landing sheet was chosen and all baselines were snapshotted. */
  landingResolved: 7,
  /**
   * `scenarios.config.json` loaded and was readable — it decides which sheets a
   * filing HIDES, so the data fetch waits for it and a failure is fatal (ASSET /
   * PARSE / NET), never a silent fall-back to "no scenarios".
   *
   * Numbered 8 and marked BEFORE steps 3-7 on purpose: step numbers are a stable
   * vocabulary quoted in docs and field reports, so a new step takes the next free
   * number instead of renumbering the others. A ledger that reaches 1, 2 and 8 and
   * stops means the scenarios file loaded and the data fetch is where it died.
   */
  scenariosLoaded: 8,
} as const;

/**
 * A single sheet's load pipeline, in execution order. Every one of the 26 role
 * components runs these same seven steps, which is what makes one shared code
 * readable across all of them.
 *
 * Step 1 is the one worth knowing by sight: it is the await on the container's
 * MBRS hydration flag. A `STALL` whose console line reads `steps reached: []`
 * is the perpetual-spinner defect, and says the sheet never got past that await.
 */
export const SHEET_LOAD_STEPS = {
  /** The container's MBRS-data hydration finished (or was never running). */
  hydrationSettled: 1,
  /** The accounting standard was resolved and a schema file chosen from it. */
  schemaSelected: 2,
  /** The schema asset request answered OK. */
  schemaFetched: 3,
  /** The schema JSON parsed, and any cross-sheet schemas it references preloaded. */
  schemaParsed: 4,
  /** The reactive form was built from that schema. */
  formBuilt: 5,
  /** Saved state was replayed and the edit pipeline wired up. */
  stateRestored: 6,
  /** Finalization ran: loading flag cleared, existing errors revealed. */
  finalized: 7,
} as const;

/** The terminal step of each pipeline — reaching it means the load succeeded. */
export const CONTAINER_TERMINAL_STEP = CONTAINER_LOAD_STEPS.landingResolved;
export const SHEET_TERMINAL_STEP = SHEET_LOAD_STEPS.finalized;

/**
 * How long a load may run before the watchdog declares it stalled and shows the
 * `STALL` toast.
 *
 * A full hydration is one HTTP fetch plus per-sheet populate writes and
 * completes in low single-digit seconds even on a cold, slow connection. 15s
 * (P.O., 2026-10-12; was 30s) is still well past "slow", so the watchdog does
 * not cry wolf on a merely sluggish load, while a preparer facing a load that
 * will never resolve gets the code sooner. Do not go near single digits: a
 * 5s value once shipped by accident and toasted `STALL` over ordinary slow
 * loads that then finished silently.
 *
 * The dev-only hydration tripwire in `FilingFormStateService` stays at 30s on
 * purpose. It prints to a console nobody has open in production; this one
 * puts a screenshot-able code in front of the reporter, and its timing is the
 * P.O.'s call.
 *
 * Deliberately NOT in `debounce-times.util.ts` — that file is for tuned RxJS
 * timings that shape behaviour. This value shapes nothing except when we give
 * up waiting.
 */
export const LOAD_WATCHDOG_MS = 15_000;

/**
 * Build the diagnostic code that goes in the toast.
 *
 * The step ledger is deliberately NOT part of this string — see this file's
 * top docblock. Pass it to {@link formatStepsReachedForConsole} instead, for
 * the console line logged alongside this code.
 *
 * @param scope        `'S<role>'` — the sheet that failed, or the sheet a
 *                     failed conversion-wide load was reaching.
 *                     (`'C'`/`'C<n>'` were retired 2026-10-12.)
 * @param failure      How the load ended.
 * @param conversionId The full conversion id, or null when there is none yet.
 */
export function formatLoadDiagnosticCode(
  scope: string,
  failure: LoadFailureClass,
  conversionId: string | null,
): string {
  const conversionSuffix = conversionId
    ? `-${conversionId.replace(/-/g, '').slice(0, 8)}`
    : '';
  return `MBRS-${toDiagnosticScopeToken(scope)}-${failure}${conversionSuffix}`;
}

/**
 * The scope as it is printed in a code: every `-` inside it becomes `_`.
 *
 * `-` is the code's FIELD separator, so it must never appear inside a field.
 * Role labels carry one (`15-410000`), and before this a code such as
 * `MBRS-S15-410000-NET-1a2b3c4d` could not be split on `-` without already
 * knowing every scope — it now reads `MBRS-S15_410000-NET-1a2b3c4d`, which
 * splits into exactly prefix / scope / class / conversion.
 *
 * Applied here, at the one place every code is assembled, rather than at each
 * source: role labels keep their hyphen everywhere else (file names, schema
 * roles, `SHEET_DIAGNOSTIC_ROLE_LABELS`), and a future scope written with a
 * hyphen cannot break the invariant. Page scopes are already written with
 * `_` at their call sites (`XBRL_FILES`), so a code found in a report greps
 * straight to the code that raised it.
 */
export function toDiagnosticScopeToken(scope: string): string {
  return scope.replace(/-/g, '_');
}

/**
 * Failure classes whose TOAST code carries no resource identifiers — no sheet
 * scope and no conversion id, just `MBRS-<CLASS>`.
 *
 * `PERM` (P.O., 2026-10-12): a 403 means this user is not allowed to see the
 * resource, so the toast must not show them which sheet or which conversion
 * it was. `MBRS-S1-PERM-01M45NK5` becomes `MBRS-PERM`.
 *
 * `AUTH` (P.O., 2026-10-15): every session-ended toast reads the same
 * `MBRS-AUTH`, whichever page, sheet or request noticed first — one code for
 * one situation, instead of `MBRS-S7-AUTH-12345abc` / `MBRS-PROFILE-AUTH` /
 * `MBRS-AUTH` depending on where the user happened to be.
 *
 * `RESP` (P.O.): the server rejected the request (a missing conversion reads
 * "We couldn't find the conversion for this record"), and the user has no use
 * for the sheet scope or conversion id. `MBRS-S13-RESP-a1b2c3d4` becomes
 * `MBRS-RESP`.
 *
 * `NET` (P.O.): a lost connection is the same situation wherever it struck, so
 * the toast reads `MBRS-NET`, never `MBRS-S4-NET-a1b2c3d4` or
 * `MBRS-XBRL_FILES-NET`. The scope and conversion stay in the console record.
 *
 * Developer Note (TODO): the P.O. intends the bare `MBRS-RESP` only for "a
 * resource was not found", but this set is keyed on the failure CLASS, and
 * `classifyHttpStatus` maps every 4xx other than 401/403 to `RESP`. Today the
 * conversion module is the only path that raises a not-found `RESP`, so the
 * two coincide; any other 4xx page-load toast (a 400 on the dashboard, say)
 * is also reduced to `MBRS-RESP`. When another resource can be not-found, or
 * the requirement narrows or expands, key this on the cause (`errorCode` /
 * 404) instead of the class.
 *
 * The console record keeps the full {@link formatLoadDiagnosticCode} code, so a
 * developer with the machine in hand can still say which conversion it was.
 */
export const IDENTIFIER_FREE_FAILURE_CLASSES: ReadonlySet<LoadFailureClass> =
  new Set<LoadFailureClass>(['PERM', 'AUTH', 'RESP', 'NET']);

/**
 * The code shown to the user in the toast (and on the stay-on-page panel).
 * Same as {@link formatLoadDiagnosticCode}, except that a class in
 * {@link IDENTIFIER_FREE_FAILURE_CLASSES} drops its scope and conversion id.
 */
export function formatToastDiagnosticCode(
  scope: string,
  failure: LoadFailureClass,
  conversionId: string | null,
): string {
  return IDENTIFIER_FREE_FAILURE_CLASSES.has(failure)
    ? `MBRS-${failure}`
    : formatLoadDiagnosticCode(scope, failure, conversionId);
}

/**
 * Render the step ledger for the console line — the one place this
 * information still appears, now that the toast's code carries no digits. See
 * {@link formatLoadDiagnosticCode}.
 *
 * @param stepsReached The steps that COMPLETED, in any order — sorted here so
 *                      the printed digits always read low-to-high and two
 *                      reports of the same failure log the same string.
 */
export function formatStepsReachedForConsole(stepsReached: readonly number[]): string {
  return `[${[...stepsReached].sort((left, right) => left - right).join(',')}]`;
}

/**
 * Classify an HTTP status into a transport failure class.
 *
 * `0` is Angular's `HttpErrorResponse` sentinel for "the request never reached
 * the server", which is precisely the network-outage case the granular toast
 * was asked to tell apart from everything else — so it gets its own class
 * rather than being folded into a generic server error.
 *
 * @param status The `HttpErrorResponse.status`, or the envelope's `statusCode`.
 */
export function classifyHttpStatus(status: number): LoadFailureClass {
  // `!status` rather than `=== NetworkError` so an absent or non-numeric status
  // lands on the same branch as the explicit 0 sentinel — both mean "no usable
  // response came back", and neither should fall through to a wrong class.
  if (!status || status === HttpStatus.NetworkError) return 'NET';
  if (status === HttpStatus.Unauthorized) return 'AUTH';
  if (status === HttpStatus.Forbidden) return 'PERM';
  if (status >= HttpStatus.InternalServerError) return 'SRV';
  if (status >= HttpStatus.BadRequest) return 'RESP';
  // Below 400 yet on a failure path (an unparseable 2xx body, say): not a
  // rejection, so not RESP. SRV, with the exact status left to the console.
  return 'SRV';
}
